import React from 'react';
import type { WizardNavigationConfig } from '../../types/WizardTypes';

interface WizardNavigationProps {
  currentStep: number;
  totalSteps: number;
  isLoading: boolean;
  onNext: () => Promise<boolean> | boolean;
  onPrevious: () => Promise<boolean> | boolean;
  onSubmit: () => Promise<void> | void;
  onCancel?: () => void;
  config: WizardNavigationConfig;
  hasErrors: boolean;
}

/**
 * WizardNavigation - Navigation controls for form wizard
 * 
 * Features:
 * - Next/Previous/Submit/Cancel buttons
 * - Loading states and disabled states
 * - Responsive design
 * - Customizable button text
 */
export const WizardNavigation: React.FC<WizardNavigationProps> = ({
  currentStep,
  totalSteps,
  isLoading,
  onNext,
  onPrevious,
  onSubmit,
  onCancel,
  config,
  hasErrors
}) => {
  
  const {
    showNext = true,
    showPrevious = true,
    showSubmit = true,
    showCancel = false,
    nextButtonText = 'Next',
    previousButtonText = 'Previous',
    submitButtonText = 'Submit',
    cancelButtonText = 'Cancel'
  } = config;

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  // Button click handlers
  const handleNext = async () => {
    if (isLoading) return;
    await onNext();
  };

  const handlePrevious = async () => {
    if (isLoading) return;
    await onPrevious();
  };

  const handleSubmit = async () => {
    if (isLoading) return;
    await onSubmit();
  };

  const handleCancel = () => {
    if (isLoading) return;
    onCancel?.();
  };

  return (
    <div className="tw-border-t tw-border-gray-200 dark:tw-border-gray-700 tw-bg-gray-50 dark:tw-bg-surface-dark tw-px-6 tw-py-4">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
        {/* Left side - Previous button */}
        <div className="tw-flex tw-items-center tw-gap-3">
          {showPrevious && !isFirstStep && (
            <button
              type="button"
              onClick={handlePrevious}
              disabled={isLoading}
              className="tw-inline-flex tw-items-center tw-justify-center tw-px-4 tw-py-2 tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-text-sm tw-font-medium tw-rounded-lg tw-text-gray-700 dark:tw-text-gray-300 tw-bg-white dark:tw-bg-surface-dark hover:tw-bg-gray-50 dark:hover:tw-bg-gray-800 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-ring-offset-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
            >
              <span className="material-symbols-outlined tw-mr-2 tw-text-base">
                arrow_back
              </span>
              {previousButtonText}
            </button>
          )}

          {showCancel && (
            <button
              type="button"
              onClick={handleCancel}
              disabled={isLoading}
              className="tw-inline-flex tw-items-center tw-justify-center tw-px-4 tw-py-2 tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-text-sm tw-font-medium tw-rounded-lg tw-text-gray-700 dark:tw-text-gray-300 tw-bg-white dark:tw-bg-surface-dark hover:tw-bg-gray-50 dark:hover:tw-bg-gray-800 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-500 focus:tw-ring-offset-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
            >
              <span className="material-symbols-outlined tw-mr-2 tw-text-base">
                close
              </span>
              {cancelButtonText}
            </button>
          )}
        </div>

        {/* Right side - Next/Submit button */}
        <div className="tw-flex tw-items-center tw-gap-3">
          {/* Step indicator for mobile */}
          <div className="tw-hidden tw-text-sm tw-text-gray-500 dark:tw-text-gray-400 md:tw-block">
            {currentStep + 1} / {totalSteps}
          </div>

          {/* Error indicator */}
          {hasErrors && (
            <div className="tw-flex tw-items-center tw-text-sm tw-text-red-600 dark:tw-text-red-400">
              <span className="material-symbols-outlined tw-mr-1 tw-text-base">
                error
              </span>
              <span className="tw-hidden sm:tw-inline">Please fix errors</span>
            </div>
          )}

          {/* Next or Submit button */}
          {isLastStep ? (
            showSubmit && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading || hasErrors}
                className="tw-inline-flex tw-items-center tw-justify-center tw-px-6 tw-py-2 tw-border tw-border-transparent tw-text-sm tw-font-medium tw-rounded-lg tw-text-white tw-bg-primary hover:tw-bg-primary/90 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-ring-offset-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors tw-min-w-[100px]"
              >
                {isLoading ? (
                  <>
                    <span className="tw-animate-spin tw-mr-2">
                      <span className="material-symbols-outlined tw-text-base">
                        refresh
                      </span>
                    </span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined tw-mr-2 tw-text-base">
                      check
                    </span>
                    {submitButtonText}
                  </>
                )}
              </button>
            )
          ) : (
            showNext && (
              <button
                type="button"
                onClick={handleNext}
                disabled={isLoading}
                className="tw-inline-flex tw-items-center tw-justify-center tw-px-6 tw-py-2 tw-border tw-border-transparent tw-text-sm tw-font-medium tw-rounded-lg tw-text-white tw-bg-primary hover:tw-bg-primary/90 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-ring-offset-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors tw-min-w-[100px]"
              >
                {isLoading ? (
                  <>
                    <span className="tw-animate-spin tw-mr-2">
                      <span className="material-symbols-outlined tw-text-base">
                        refresh
                      </span>
                    </span>
                    Loading...
                  </>
                ) : (
                  <>
                    {nextButtonText}
                    <span className="material-symbols-outlined tw-ml-2 tw-text-base">
                      arrow_forward
                    </span>
                  </>
                )}
              </button>
            )
          )}
        </div>
      </div>

      {/* Mobile step indicator */}
      <div className="tw-mt-3 tw-text-center tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 md:tw-hidden">
        Step {currentStep + 1} of {totalSteps}
      </div>
    </div>
  );
};